'use client';

import { QuestionFillIcon, ArrowRightIcon, CloseBigIcon } from '@/components/icons/svg-icons';
import { getBetTypeTutorialSetting, getNewbieSportSettings } from '@/services/settings.service';
import { customizableMainBetTypeSports } from '@/settings/sports-settings';
import { useSiteStore } from '@/store/site';
import { MarketLabel } from '@/types';
import { SportSetting } from '@/types/settings';
import { Button } from '@nf/ui';
import { Box, Maybe, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { usePreferredMainBetType } from '@/features/odds-page/hooks/use-preferred-main-bet-type';
import { switchBettypeStyle } from '../switch-bettype/switch-bettype.style';
import { betTypeService } from '@/services/bettype.service';
import { oddsPageService } from '@/features/odds-page/services/odds-page.service';
import { TranslateType } from '@/types/bet-type-settings';
import { useMotionValueEvent, useScroll } from 'framer-motion';
import { useTargetScrollableContext } from '@/components/containers/target-scrollable-container';
import { modalService } from '@/services/modal.service';
import webStorageKeys from '@/constants/web-storage-keys';
import { uiService } from '@/services/ui.service';
import { useOddsTableMarsContext } from '../../context';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';

export const SwitchBetType = () => {
	const { sport, gameType, market, isMyFav } = useOddsTableMarsContext();
	const { breakpoint } = useBreakpoint();
	const scrollableRef = useTargetScrollableContext();
	const [isFixed, setIsFixed] = useState<boolean>(false);
	const { scrollY } = useScroll({ container: scrollableRef });
	const { scrollY: bodyScrollY } = useScroll();
	const [isShowTip, setIsShowTip] = useState(false);
	const sportSetting: SportSetting = getNewbieSportSettings(sport);
	const t = useTranslations('General');
	const betTypeTranslate = useTranslations('BetType');
	const betTypeItems = sportSetting?.oddsPageFilterBetTypes ?? [];
	const [betTypeFilterCriterion, setBetTypeFilterCriterion] = useSiteStore(
		state => [state.getBetTypeFilterCriterion(sport), state.setBetTypeFilterCriterion],
	);
	const stickyRef = useRef<HTMLDivElement>(null);
	const preferredMainBetType = usePreferredMainBetType();

	let sortedMainBetTypeItems;
	if (customizableMainBetTypeSports.includes(sport)) {
		const mainBetTypeOrder = oddsPageService.getSortedMainBetTypes(sport, preferredMainBetType);
		sortedMainBetTypeItems = [...betTypeItems].sort((a, b) => {
			const betTypeA = a.betTypes[0];
			const betTypeB = b.betTypes[0];
			
			return mainBetTypeOrder.indexOf(betTypeA) - mainBetTypeOrder.indexOf(betTypeB);
		});
	} else {
		sortedMainBetTypeItems = betTypeItems; 
	}

	const filteredBetTypeSettings = sortedMainBetTypeItems.filter(betTypeSetting => {
		const isValidMarket = itemsEmptyCheck(betTypeSetting.limitedMarkets ?? [])
			.fold(
				() => true,
				() => {
					return (betTypeSetting.limitedMarkets as MarketLabel[]).includes(market);
				}
			);
		const isValidGameType = matchEquals(gameType)
			.on('FX', () => betTypeSetting.group === 1)
			.on('FC', () => betTypeSetting.group === 2)
			.otherwise(() => true)
		return isValidMarket && isValidGameType;
	})

	const getFixHeaightPoint = () => MaybeMatch(breakpoint >= Breakpoint.md)
		.fold(
			() => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) + (stickyRef.current?.offsetHeight ?? 0),
			() => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) + parseInt(getComputedStyle(document.documentElement).getPropertyValue('--page-header-height'))
		)

	// for desktop: set is fixed when scroll
	useMotionValueEvent(scrollY, 'change', (latest) => {
		setIsFixed(latest > 0 && ((stickyRef.current?.getBoundingClientRect().top ?? 0) <= getFixHeaightPoint()))
	});

	useMotionValueEvent(bodyScrollY, 'change', (latest) => {
		setIsFixed(latest > 0 && ((stickyRef.current?.getBoundingClientRect().top ?? 0) <= getFixHeaightPoint()))
	});

	useEffect(() => {
		const scrollHeight = breakpoint >= Breakpoint.md ? scrollY.get() : bodyScrollY.get();
		setIsFixed(scrollHeight > 0 && stickyRef.current?.getBoundingClientRect().top === getFixHeaightPoint())
	}, [breakpoint])


	useEffect(() => {
		if (typeof window !== 'undefined' && window.localStorage) {
			const newbieTutorial = localStorage.getItem(webStorageKeys.HAS_SHOWN_NEWBIE_TUTORIAL_INFO) || false;
			setIsShowTip(!Boolean(newbieTutorial))
		}

		Maybe(betTypeFilterCriterion)
			.chain(() => MaybeMatch(filteredBetTypeSettings.findIndex(criterionSetting => criterionSetting.betTypes.toString() === betTypeFilterCriterion?.betTypes?.toString()) > -1))
			.fold(
				() => setBetTypeFilterCriterion(sport, filteredBetTypeSettings[0]),
				() => {}
			)
	}, [sport, gameType])

	const getBetTypeNameBySport = (betTypeId: number, sportId: number, resourceId: string | undefined) => {
		
		if (Number(sport) ===8 && [20, 8800, 8801].includes(betTypeId)) {
			switch (betTypeId) {
				case 20:
					return t('lbl_FilterML');
				case 8800:
					return t('lbl_FilterRLHDP');
				case 8801:
					return t('lbl_FilterTRG');
			}
		}

		return Box(betTypeService.getBetTypeSettingTitle(betTypeId, sportId))
			.map(([label, translateType]: [string, TranslateType]) => matchEquals(translateType)
				.on('General', () => t.raw(label))
				.on('BetType', () => betTypeTranslate(label))
				.otherwise(() => '')
			)
			.map((label: string) => resourceId ? label.replace('{0}', resourceId) : label)
			.extract()
	} 
	const tutorialSetting = getBetTypeTutorialSetting(betTypeFilterCriterion?.betTypes?.[0] ?? 0)  ;
	const hasTutorial = !isMyFav && tutorialSetting && sport !== 8;

	const closeTip = () => {
		setIsShowTip(false);
		localStorage.setItem(webStorageKeys.HAS_SHOWN_NEWBIE_TUTORIAL_INFO, 'true');
		uiService.displayTutorialToolTip();
	}

	// fancy not show, but still need to set bet type to store
	if (gameType === 'FC') return null;
	
	return (
		<>
			<section className={switchBettypeStyle.root}>
				<div className={switchBettypeStyle.filterWrap} data-fixed={isFixed} ref={stickyRef}>
					<div className={switchBettypeStyle.filter}>
						{
							filteredBetTypeSettings.map(setting => {
								return (
									<div
										key={setting.betTypes.toString()}
										className={clsx(switchBettypeStyle.filterItem, { [switchBettypeStyle.filterItemWithTip]: (hasTutorial && isShowTip) })}
										data-active={betTypeFilterCriterion?.betTypes?.toString() === setting.betTypes.toString()}
										onClick={() => setBetTypeFilterCriterion(sport, setting)}
									>
										{ getBetTypeNameBySport(setting.betTypes[0], sport, setting.resourceId) }
									</div>
								);
							})
						}
					</div>
				</div>
				{
					hasTutorial && (
						<div className={switchBettypeStyle.tipWrap} data-active={isShowTip}>
							<div className={switchBettypeStyle.tip}>
								<div className={switchBettypeStyle.tipIcon}>
									<QuestionFillIcon />
								</div>
								<div className={switchBettypeStyle.tipMessage}>
									<div className={switchBettypeStyle.tipText}>
										{t(tutorialSetting.title)}
									</div>
									{
										tutorialSetting.description && (
											<Button className={switchBettypeStyle.button} onClick={() => modalService.openNewbieTutorialModal()}>
												<div className={switchBettypeStyle.buttonText}>{t(tutorialSetting.description)}</div>
												<ArrowRightIcon />
											</Button>
										)
									}
								</div>
								<Button variant="icon" className={switchBettypeStyle.closeButton} onClick={closeTip}>
									<CloseBigIcon />
								</Button>
							</div>
						</div>
					)
				}
			</section>
		</>
	)
}
